import { PropsWithChildren, SubmitEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from '@connect-x-app/hybrid-state';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { ConfirmationModal, Modal, ModalOpener } from '@connect-x-app/web-ui';

import { Button, FlexComponent } from '../../../../shared';

type ModalFormProps = PropsWithChildren & {
  disabled: boolean;
  headerTitle: string;
  isDirty?: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (event: SubmitEvent) => void;
  submitButtonText?: string;
  testId?: string;
};

export const ModalForm = ({
  children,
  disabled,
  headerTitle,
  isDirty,
  isSubmitting,
  onCancel,
  onSubmit,
  submitButtonText,
  testId,
}: ModalFormProps) => {
  const { t } = useTranslation();
  const { setHasUnsavedChanges } = useUnsavedChanges();

  useEffect(() => {
    setHasUnsavedChanges(isDirty ?? false);
    return () => {
      setHasUnsavedChanges(false);
    };
  }, [isDirty, setHasUnsavedChanges]);

  const handleCancel = () => {
    if (isDirty) {
      ModalOpener.instance.open({
        render: () => (
          <ConfirmationModal
            cancelButtonTheme="primary"
            cancelText={t('NAVIGATION_GUARD.KEEP')}
            confirmButtonTheme="negative"
            confirmText={t('NAVIGATION_GUARD.CONFIRM')}
            content={t('NAVIGATION_GUARD.TITLE')}
            onCancel={() => ModalOpener.instance.close()}
            onConfirm={() => {
              onCancel();
              ModalOpener.instance.close();
            }}
            swapButtons
          />
        ),
      });
    } else {
      onCancel();
    }
  };

  return (
    <Modal onCancel={handleCancel} testId={testId}>
      <Modal.Header onCancel={handleCancel}>{headerTitle}</Modal.Header>
      <form onSubmit={onSubmit}>
        <FlexComponent flexDirection="column" gap={1} padding={2} width="full">
          {children}
        </FlexComponent>
        <Modal.Buttons>
          <Button isOutline onClick={handleCancel} testId={ADMIN_TEST_IDS.roles.formCancel} theme="transparent">
            {t('SHARED.BUTTONS.CANCEL')}
          </Button>
          <Button
            disabled={isSubmitting || disabled}
            loading={isSubmitting}
            testId={ADMIN_TEST_IDS.roles.formSubmit}
            theme="primary"
            type="submit"
          >
            {submitButtonText ?? t('SHARED.BUTTONS.ADD')}
          </Button>
        </Modal.Buttons>
      </form>
    </Modal>
  );
};
